import { z } from "zod";

export const CITY_UF_REGEX =
  /^\p{Lu}[\p{L}\p{M}\s.'-]*\s-\s[A-Z]{2}(,\s?Brasil)?$/u;

export const ticketFormSchema = z
  .object({
    officeType: z.enum(["pronto", "personalizado-sob-medida", "ambos"]),
    reasonOpening: z.string({ required_error: "Obrigatório" }),
    requiresDocs: z.boolean(),
    selectedDocs: z.array(z.string()),
    otherDocText: z.string().optional(),
    size: z.string().optional(),
    positions: z.coerce.number().positive("Obrigatório"),
    // Membership in HubSpot's list is checked by `createTicketFormSchema`,
    // since the list is fetched at runtime (PED-14049).
    city: z.string({ required_error: "Obrigatório" }).min(1, "Obrigatório"),
    neighborhoodText: z.string().optional(),
    budget: z.string().min(1, "Obrigatório"),
    expectedEntryDate: z.string().min(1, "Obrigatório"),
    observations: z.string().optional(),
    searchMoreOptions: z.string().optional(),
    contractMonths: z.string().optional(),
    openToNearby: z.string().optional(),
    privateMeetingRooms: z.string().optional(),
    meetingRoomCapacities: z.array(
      z.object({
        capacity: z.coerce.number().positive("Informe a capacidade"),
      }),
    ),
    willingToAdapt: z.string().optional(),
    specificPartner: z.string().optional(),
    roomCount: z.number(),
    roomVisitsComplete: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    const isCustom =
      data.officeType === "personalizado-sob-medida" ||
      data.officeType === "ambos";

    if (!data.reasonOpening) {
      ctx.addIssue({
        code: "custom",
        path: ["reasonOpening"],
        message: "Obrigatório",
      });
    }

    if (data.requiresDocs && data.selectedDocs.length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["selectedDocs"],
        message: "Selecione ao menos um documento",
      });
    }

    if (
      data.requiresDocs &&
      data.selectedDocs.includes("outro") &&
      !data.otherDocText?.trim()
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["otherDocText"],
        message: "Obrigatório",
      });
    }

    if (
      data.reasonOpening === "agendamento_visitas" &&
      data.roomCount > 0 &&
      !data.roomVisitsComplete
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["roomVisitsComplete"],
        message: "Selecione data e horário para todas as salas",
      });
    }

    if (
      data.roomCount > 0 &&
      !["sim", "nao"].includes(data.searchMoreOptions ?? "")
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["searchMoreOptions"],
        message: "Obrigatório",
      });
    }

    if (isCustom) {
      if (!data.contractMonths) {
        ctx.addIssue({
          code: "custom",
          path: ["contractMonths"],
          message: "Obrigatório",
        });
      }
      if (!["sim", "nao"].includes(data.openToNearby ?? "")) {
        ctx.addIssue({
          code: "custom",
          path: ["openToNearby"],
          message: "Obrigatório",
        });
      }
      if (!["sim", "nao"].includes(data.privateMeetingRooms ?? "")) {
        ctx.addIssue({
          code: "custom",
          path: ["privateMeetingRooms"],
          message: "Obrigatório",
        });
      }
      if (
        data.privateMeetingRooms === "sim" &&
        (data.meetingRoomCapacities.length === 0 ||
          data.meetingRoomCapacities.some((r) => r.capacity <= 0))
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["meetingRoomCapacities"],
          message: "Informe a capacidade de cada sala",
        });
      }
    }
  });

export type TicketFormValues = z.infer<typeof ticketFormSchema>;

/**
 * The form schema bound to HubSpot's city options: the Zap rejects any other
 * value, and the old regex let typos like "Santro Andre - SP" through
 * (PED-14049). An empty list (not loaded yet, or failed) accepts no city.
 */
export function createTicketFormSchema(cityValues: readonly string[]) {
  return ticketFormSchema.superRefine((data, ctx) => {
    if (data.city && !cityValues.includes(data.city)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["city"],
        message: "Selecione uma cidade da lista",
      });
    }
  });
}
